import { WSList, WSPanel, WSText } from "@wingspanhq/fe-component-library";
import React, { useMemo } from "react";
import { WSQueries } from "../../../../query/WSQuery";
import { getBulkQueriesByResource } from "../../query/bulkBatchResourceQueryMap";
import { BulkResource } from "../../types";

import { BulkStatus } from "@wingspanhq/payments/dist/interfaces";
import {
  isBatchFullyImported,
  isBatchPartiallyImported,
  isUploadFinished,
  isUploadInProgress
} from "../../utils/bulkBatchUtils";
import { Steps } from "../../../../shared/components/Steps";

type Props = {
  bulkBatchId: string;
  bulkResource: BulkResource;
};

export const BulkBatchImportSteps: React.FC<Props> = ({
  bulkBatchId,
  bulkResource
}) => {
  const { useBulkBatch, useBulkBatchSummary } =
    getBulkQueriesByResource(bulkResource);
  const queryBulkBatch = useBulkBatch(bulkBatchId);
  const queryBulkSummary = useBulkBatchSummary(bulkBatchId);
  const isEmptyBatch = queryBulkSummary.data?.listSize === 0;

  const uploadFileStepStatus = useMemo(() => {
    if (isUploadFinished(queryBulkBatch.data, isEmptyBatch)) {
      return "Complete";
    } else if (isUploadInProgress(queryBulkBatch.data)) {
      return "InProgress";
    }
    return "Active";
  }, [queryBulkBatch.data, isEmptyBatch]);

  const completeImportStepStatus = useMemo(() => {
    if (isBatchFullyImported(queryBulkBatch.data)) {
      return "Complete";
    } else if (isBatchPartiallyImported(queryBulkBatch.data)) {
      return "Warning";
    } else if (queryBulkBatch.data?.status === "Failed") {
      return "Failed";
    } else if (
      [BulkStatus.Pending, BulkStatus.Processing].includes(
        queryBulkBatch.data?.status as BulkStatus
      )
    ) {
      return "InProgress";
    } else if (isUploadFinished(queryBulkBatch.data, isEmptyBatch)) {
      return "Active";
    }
    return "Pending";
  }, [queryBulkBatch.data, isEmptyBatch]);
  return (
    <WSQueries queries={{ queryBulkBatch }}>
      {({ queryBulkBatchData: bulkBatch }) => {
        return (
          <WSPanel>
            <WSList gap="L">
              <WSText.Paragraph weight="medium" color="gray600">
                Batch import steps
              </WSText.Paragraph>
              <Steps
                steps={[
                  { title: "Create batch", status: "Complete" },
                  { title: "Upload spreadsheet", status: uploadFileStepStatus },
                  { title: "Complete import", status: completeImportStepStatus }
                ]}
              />
            </WSList>
          </WSPanel>
        );
      }}
    </WSQueries>
  );
};
